/**
 * G-101 — a page that outlives a deploy, and the one reload that rescues it.
 *
 * WHAT HAPPENS TODAY. The app is code-split, and every chunk filename carries
 * a content hash. A tablet's WebView stays open for days; a deploy replaces
 * the built assets and the old filenames stop existing. The JS already running
 * still asks for the OLD names, so every lazy import from that moment on gets
 * a 404 — for as long as nobody restarts the app.
 *
 * It bit the shop on 2026-09-22: a florist's Russian never arrived because
 * `loadLanguage` could not fetch its chunk, so the language selector said
 * Russian and every word stayed English. `LanguageProvider` catches that
 * failure by design, which is why it was silent. The barcode scanner is lazy
 * too, and its failure would be a camera that never opens.
 *
 * WHY A RELOAD IS THE RIGHT ANSWER. There is nothing to repair in the page:
 * the build it belongs to is gone. `index.html` is served `no-cache`, so one
 * reload lands on the current build and everything resolves again. The florist
 * loses whatever panel they had open, which is the cost, and it is far smaller
 * than a kiosk that quietly stops being able to open half its screens.
 *
 * ONCE PER PAGE LOAD, AND THAT GUARD IS LOAD-BEARING. A failed import is not
 * proof of a deploy — a tablet whose wifi has dropped fails identically. With
 * no guard, an offline kiosk would reload, fail, reload, for as long as the
 * connection is down, which is worse than the bug. The flag lives in
 * `sessionStorage`, so it survives the reload it causes and is gone when the
 * app is next started properly; a second failure in the same session falls
 * through to whatever the caller already does about it.
 */

const RELOADED_KEY = "fw-stale-build-reloaded";

/**
 * What this needs from a browser, and no more — so a test can hand it a fake
 * and drive the one case that matters (the second failure), which no real
 * window would let us reach twice.
 */
export interface StaleBuildHost {
  addEventListener(
    type: "vite:preloadError",
    listener: (event: { preventDefault(): void }) => void,
  ): void;
  location: { reload(): void };
  sessionStorage: Pick<Storage, "getItem" | "setItem">;
}

export function installStaleBuildRecovery(host?: StaleBuildHost): void {
  const target =
    host ?? (typeof window === "undefined" ? null : (window as unknown as StaleBuildHost));
  if (!target) return;

  /** Storage can throw (private mode, blocked site data) — never break on it. */
  const alreadyReloaded = (): boolean => {
    try {
      return target.sessionStorage.getItem(RELOADED_KEY) === "1";
    } catch {
      // Unable to REMEMBER is unable to bound the loop, so the honest answer
      // is "already tried": no reload at all beats a reload we cannot stop.
      return true;
    }
  };

  /**
   * Vite fires this when a dynamic import's preload cannot be fetched, which
   * is the stale-deploy case and little else. It is far narrower than catching
   * every rejected promise, so it is the only signal acted on here.
   */
  target.addEventListener("vite:preloadError", (event) => {
    if (alreadyReloaded()) {
      // Loudly enough for G-72's console buffer to carry it into a bug report:
      // a second failure means the chunk is unreachable for a reason a reload
      // cannot fix, and that is the useful fact.
      console.warn(
        "A module failed to load and this page has already reloaded once — not reloading again.",
      );
      return;
    }
    try {
      target.sessionStorage.setItem(RELOADED_KEY, "1");
    } catch {
      // Cannot remember, so cannot promise this happens only once. Refuse
      // rather than risk the loop.
      console.warn("A module failed to load; cannot record a reload, so not reloading.");
      return;
    }
    // Vite's own default for this event is to throw; the reload is the remedy.
    event.preventDefault();
    console.warn("A module failed to load — reloading onto the current build.");
    target.location.reload();
  });
}
